import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Avatar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Badge,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  ListItemText,
  Checkbox,
} from '@mui/material';
import {
  Search as SearchIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Quiz as QuizIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PlayCircleOutline as PlayCircleOutlineIcon,
  FilterList as FilterListIcon,
  Assessment as AssessmentIcon,
  School as SchoolIcon,
  Timeline as TimelineIcon,
  Score as ScoreIcon,
} from '@mui/icons-material';
import {
  useGetAllExamAttemptsQuery,
  useDeleteExamAttemptMutation,
  useResetExamAttemptMutation,
  useGetAdminExamsQuery,
  useGetUsersQuery,
} from '@/store/api/adminApi';
import { ExamStatusBadge } from '@/components/common';

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`attempts-tabpanel-${index}`}
      aria-labelledby={`attempts-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const ExamAttemptsManagement = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    status: 'all',
    examId: 'all',
    studentId: 'all',
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [attemptToDelete, setAttemptToDelete] = useState(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [attemptToReset, setAttemptToReset] = useState(null);

  // API calls
  const { data: attemptsData, isLoading: attemptsLoading, refetch } = useGetAllExamAttemptsQuery();
  const { data: examsData } = useGetAdminExamsQuery();
  const { data: usersData } = useGetUsersQuery({ role: 'student' });
  const [deleteAttempt, { isLoading: isDeleting }] = useDeleteExamAttemptMutation();
  const [resetAttempt, { isLoading: isResetting }] = useResetExamAttemptMutation();

  const attempts = attemptsData?.data || [];
  const exams = examsData?.data || [];
  const students = usersData?.data || [];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleDeleteClick = (attempt) => {
    setAttemptToDelete(attempt);
    setDeleteDialogOpen(true);
  };

  const handleResetClick = (attempt) => {
    setAttemptToReset(attempt);
    setResetDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteAttempt(attemptToDelete._id).unwrap();
      setDeleteDialogOpen(false);
      setAttemptToDelete(null);
      refetch();
    } catch (error) {
      console.error('Error deleting attempt:', error);
    }
  };

  const handleResetConfirm = async () => {
    try {
      await resetAttempt(attemptToReset._id).unwrap();
      setResetDialogOpen(false);
      setAttemptToReset(null);
      refetch();
    } catch (error) {
      console.error('Error resetting attempt:', error);
    }
  };

  // Filter and search logic
  const filteredAttempts = attempts.filter(attempt => {
    const matchesSearch = 
      attempt.studentName?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      attempt.examTitle?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      attempt.studentEmail?.toLowerCase().includes(searchTerm?.toLowerCase());

    const matchesStatus = filters.status === 'all' || 
      (filters.status === 'completed' && attempt.status === 'completed') ||
      (filters.status === 'in_progress' && attempt.status === 'in_progress') ||
      (filters.status === 'abandoned' && attempt.status === 'abandoned');

    const matchesExam = filters.examId === 'all' || attempt.examId === filters.examId;
    const matchesStudent = filters.studentId === 'all' || attempt.studentId === filters.studentId;

    return matchesSearch && matchesStatus && matchesExam && matchesStudent;
  });

  // Statistics
  const totalAttempts = attempts.length;
  const completedAttempts = attempts.filter(a => a.status === 'completed').length;
  const inProgressAttempts = attempts.filter(a => a.status === 'in_progress').length;
  const averageScore = attempts.filter(a => a.score !== null && a.score !== undefined)
    .reduce((sum, a) => sum + a.score, 0) / Math.max(attempts.filter(a => a.score !== null).length, 1);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#4CAF50';
      case 'in_progress': return '#FF9800';
      case 'abandoned': return '#f44336';
      default: return '#9E9E9E';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon fontSize="small" />;
      case 'in_progress': return <PlayCircleOutlineIcon fontSize="small" />;
      case 'abandoned': return <CancelIcon fontSize="small" />;
      default: return <AccessTimeIcon fontSize="small" />;
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    }
    return `${minutes}m ${secs}s`;
  };

  return (
    <Box sx={{ p: 3, minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      {/* Header */}
      <Card
        sx={{
          mb: 3,
          background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
          color: 'white',
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AssessmentIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" fontWeight="bold">
                Exam Attempts Management
              </Typography>
              <Typography variant="subtitle1" sx={{ opacity: 0.9, mt: 1 }}>
                Monitor and manage all student exam attempts
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
              color: 'white',
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AssessmentIcon sx={{ fontSize: 30 }} />
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {totalAttempts}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Attempts
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
              color: 'white',
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircleIcon sx={{ fontSize: 30 }} />
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {completedAttempts}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Completed
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
              color: 'white',
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PlayCircleOutlineIcon sx={{ fontSize: 30 }} />
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {inProgressAttempts}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    In Progress
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
              color: 'white',
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ScoreIcon sx={{ fontSize: 30 }} />
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {Math.round(averageScore * 10) / 10}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Avg. Score
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                py: 2,
              },
              '& .Mui-selected': {
                color: '#FF9800',
              },
            }}
          >
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssessmentIcon />
                  All Attempts ({filteredAttempts.length})
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TimelineIcon />
                  Analytics
                </Box>
              }
            />
          </Tabs>
        </Box>

        {/* Filters and Search */}
        <Box sx={{ p: 3, pb: 0 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search attempts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="abandoned">Abandoned</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth>
                <InputLabel>Exam</InputLabel>
                <Select
                  value={filters.examId}
                  onChange={(e) => handleFilterChange('examId', e.target.value)}
                  label="Exam"
                >
                  <MenuItem value="all">All Exams</MenuItem>
                  {exams.map((exam) => (
                    <MenuItem key={exam._id} value={exam._id}>
                      {exam.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth>
                <InputLabel>Student</InputLabel>
                <Select
                  value={filters.studentId}
                  onChange={(e) => handleFilterChange('studentId', e.target.value)}
                  label="Student"
                >
                  <MenuItem value="all">All Students</MenuItem>
                  {students.map((student) => (
                    <MenuItem key={student._id} value={student._id}>
                      {student.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        {/* Tab Panels */}
        <TabPanel value={activeTab} index={0}>
          {/* All Attempts Tab */}
          {attemptsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Student</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Exam</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Score</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Started</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAttempts
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((attempt) => (
                      <TableRow key={attempt._id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: '#2196F3' }}>
                              <PersonIcon fontSize="small" />
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="600">
                                {attempt.studentName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {attempt.studentEmail}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="600">
                            {attempt.examTitle}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <ExamStatusBadge 
                            status={attempt.status === 'completed' ? 'completed' : 'in_progress'}
                            showIcon={true}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="600">
                            {attempt.score !== null && attempt.score !== undefined 
                              ? `${attempt.score}/990` 
                              : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDuration(attempt.duration)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(attempt.startedAt).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {attempt.status === 'in_progress' && (
                              <Tooltip title="Reset attempt">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleResetClick(attempt)}
                                  sx={{ color: '#FF9800' }}
                                >
                                  <RefreshIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Delete attempt">
                              <IconButton 
                                size="small" 
                                onClick={() => handleDeleteClick(attempt)}
                                sx={{ color: '#f44336' }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={filteredAttempts.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </TableContainer>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {/* Analytics Tab */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Completion Rate
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {Math.round((completedAttempts / Math.max(totalAttempts, 1)) * 100)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    of all attempts completed
                  </Typography>
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Average Completion Time
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {formatDuration(
                      attempts.filter(a => a.status === 'completed' && a.duration)
                        .reduce((sum, a) => sum + a.duration, 0) / 
                      Math.max(attempts.filter(a => a.status === 'completed' && a.duration).length, 1)
                    )}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this exam attempt by{' '}
            <strong>{attemptToDelete?.studentName}</strong> for{' '}
            <strong>{attemptToDelete?.examTitle}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Confirmation Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>Confirm Reset</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to reset this exam attempt by{' '}
            <strong>{attemptToReset?.studentName}</strong> for{' '}
            <strong>{attemptToReset?.examTitle}</strong>? This will allow the student to restart the exam.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleResetConfirm}
            color="warning"
            variant="contained"
            disabled={isResetting}
          >
            {isResetting ? 'Resetting...' : 'Reset'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExamAttemptsManagement;
